import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

/**
 * Card primitive. Use everywhere we need a surface container.
 *
 * Design (impeccable foundation, 2026-05-31): a hairline border + a resting
 * `e1` elevation lifts the card off the slate-50 canvas — crisper than a flat
 * border alone. The shadow transition lets interactive cards add `hover:shadow-e2`
 * for a tactile lift without re-specifying timing. Keep depth subtle; don't nest
 * cards (a card inside a card is always the wrong answer — use spacing/dividers).
 */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function Card({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          // Softer 2xl corners (reskin 2026-06-26, Run a Call alignment) — reads more premium.
          // DESIGN-CALM-01: one card — hairline only, no resting shadow. Depth
          // is for things that float (menus, drawers, a card being dragged);
          // interactive cards still add hover:shadow-e2 themselves.
          "rounded-2xl border border-hairline bg-white transition-shadow duration-200 ease-out-quart",
          className,
        )}
        {...props}
      />
    );
  },
);
